<script setup>
import { ref } from 'vue';
import { useAuthStore } from '@/stores/auth';
import {emailValidation, verifySecurePassword} from "@/utils/validations";
import LabelComponent from "@/components/utils/LabelComponent.vue";

const email = ref('');
const code = ref('');
const chpass = ref('');
const password = ref('');
const passwordConfirmation = ref('');
const errorMessage = ref(null);
const authStore = useAuthStore();
const loading = ref(false);
const steps = ref(1);

const send = async () => {

  if (!email.value) {
    errorMessage.value = 'Email is required';
    return;
  }

  if (!emailValidation(email.value)) {
    errorMessage.value = 'Invalid email';
    return;
  }

  errorMessage.value = null;
  loading.value = true;
  try {
    await authStore.recovery(email.value);
    loading.value = false;
    steps.value = 2;
  } catch (error) {
    errorMessage.value = error.message;
    loading.value = false;
  }
};

const verify = async () => {
  if (!code.value) {
    errorMessage.value = 'Code is required';
    return;
  }

  errorMessage.value = null;
  loading.value = true;

  try {
    let response = await authStore.verify(email.value, code.value);
    loading.value = false;
    steps.value = 3;
    chpass.value = response.chpass;
    console.log(response);
  } catch (error) {
    errorMessage.value = error.message;
    loading.value = false;
  }

};

const reset = async () => {
  if (!password.value) {
    errorMessage.value = 'Password is required';
    return;
  }

  // Verify secure password
  if (password.value.length < 8) {
    errorMessage.value = 'Password must be at least 8 characters';
    return;
  }

  if (!passwordConfirmation.value) {
    errorMessage.value = 'Password confirmation is required';
    return;
  }

  if(verifySecurePassword(password.value) !== true) {
    errorMessage.value = 'Password must contain at least one uppercase letter, one lowercase letter, one number and one special character';
    return;
  }

  if (password.value !== passwordConfirmation.value) {
    errorMessage.value = 'Passwords do not match';
    return;
  }

  errorMessage.value = null;
  loading.value = true;

  try {
    await authStore.reset(email.value, chpass.value, password.value);
    loading.value = false;
    steps.value = 4;
  } catch (error) {
    errorMessage.value = error.message;
    loading.value = false;
  }
};

</script>

<template>
  <Transition name="fade">
    <div class="flex items-center justify-center min-h-screen dark:bg-gray-800 bg-gray-100 px-4">
      <div class="w-full max-w-lg p-8 space-y-6 bg-white dark:bg-gray-700 bg-opacity-90 backdrop-filter backdrop-blur-lg rounded-xl shadow-xl mx-auto">
        <img src="/images/electride_app-icon.svg" alt="Logo" class="w-24 mx-auto" />
        <div v-if="steps===1">
          <h1 class="text-3xl font-bold text-center text-gray-800 dark:text-gray-500">Recovery account</h1>
          <form @submit.prevent="send" class="space-y-4">
            <!-- Email Field -->
            <div class="relative">
              <LabelComponent value="Email" for="email" />
              <input
                  v-model="email"
                  type="text"
                  id="email"
                  required
                  :readonly="loading"
                  class="w-full px-4 py-2 border border-gray-300 dark:bg-gray-700 dark:border-gray-400 dark:text-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-700 transition duration-200 ease-in-out"
                  placeholder="Insert your email"
              />
            </div>
            <!-- Submit Button -->
            <button
                type="submit"
                :disabled="loading"
                class="w-full px-4 py-2 font-semibold text-black bg-emerald-500 rounded-md hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-opacity-50 transition-transform transform hover:scale-105 duration-300 ease-in-out"
            >
              Recovery account
            </button>
            <div class="flex items-center justify-between text-center space-x-1">
              <router-link to="/login" class="text-sm text-emerald-600 dark:text-emerald-200 hover:underline font-semibold" style="font-size: 16px;">
                Login
              </router-link>
            </div>
          </form>
        </div>
        <div v-else-if="steps === 2">
          <h1 class="text-3xl font-bold text-center text-gray-800 dark:text-gray-500">Write the code</h1>
          <small>
            We have sent a code to your email, please check your inbox and write the code here
          </small>
          <form @submit.prevent="verify" class="space-y-4">
            <!-- Email Field -->
            <div class="relative">
              <input
                  v-model="code"
                  type="text"
                  id="code"
                  autofocus
                  required
                  :readonly="loading"
                  class="w-full px-4 py-2 border border-gray-300 dark:bg-gray-700 dark:border-gray-400 dark:text-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-700 transition duration-200 ease-in-out"
                  placeholder="Insert the code"
              />
            </div>
            <!-- Submit Button -->
            <button
                type="submit"
                :disabled="loading"
                class="w-full px-4 py-2 font-semibold text-black bg-emerald-500 rounded-md hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-opacity-50 transition-transform transform hover:scale-105 duration-300 ease-in-out"
            >
              Verify
            </button>
            <div class="flex items-center justify-between text-center space-x-1">
              <router-link to="/login" class="text-sm text-emerald-600 dark:text-emerald-200 hover:underline font-semibold" style="font-size: 16px;">
                Login
              </router-link>
            </div>
          </form>
        </div>
        <div v-else-if="steps === 3">
          <h1 class="text-3xl font-bold text-center text-gray-800 dark:text-gray-500">Change your password</h1>
          <small class="my-2">
            You should not refresh the page so that you do not lose the code and start the recovery process.
          </small>
          <form @submit.prevent="reset" class="space-y-4">

            <LabelComponent value="New password" for="password" />
            <!-- Email Field -->
            <div class="relative">
              <input
                  v-model="password"
                  type="password"
                  id="password"
                  autofocus
                  required
                  :readonly="loading"
                  class="w-full px-4 py-2 border border-gray-300 dark:bg-gray-700 dark:border-gray-400 dark:text-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-700 transition duration-200 ease-in-out"
                  placeholder="Insert the code"
              />
            </div>

            <LabelComponent value="Password confirmation" for="passwordConfirmation" />
            <!-- Email Field -->
            <div class="relative">
              <input
                  v-model="passwordConfirmation"
                  type="password"
                  id="passwordConfirmation"
                  autofocus
                  required
                  :readonly="loading"
                  class="w-full px-4 py-2 border border-gray-300 dark:bg-gray-700 dark:border-gray-400 dark:text-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-700 transition duration-200 ease-in-out"
                  placeholder="Insert the code"
              />

            </div>
            <!-- Submit Button -->
            <button
                type="submit"
                :disabled="loading"
                class="w-full px-4 py-2 font-semibold text-black bg-emerald-500 rounded-md hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-opacity-50 transition-transform transform hover:scale-105 duration-300 ease-in-out"
            >
              Change password
            </button>
            <div class="flex items-center justify-between text-center space-x-1">
              <router-link to="/login" class="text-sm text-emerald-600 dark:text-emerald-200 hover:underline font-semibold" style="font-size: 16px;">
                Login
              </router-link>
            </div>
          </form>
        </div>
        <div v-else>
          <h1 class="text-3xl font-bold text-center text-gray-800 dark:text-gray-500">Recovery account</h1>
          <div class="my-3">
            Your account has been recovered, please login with your new password
          </div>
          <router-link to="/login" class="text-sm text-emerald-600 dark:text-emerald-200 hover:underline font-semibold" style="font-size: 16px;">
            Go to Login
          </router-link>
        </div>
        <!-- Error Message -->
        <Transition name="slide-down">
          <p v-if="errorMessage" class="text-sm text-red-600 dark:text-red-400 text-center">{{ errorMessage }}</p>
        </Transition>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* Animaciones */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-down-enter-active {
  transition: all 0.5s ease;
}
.slide-down-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}
.slide-down-enter-to {
  transform: translateY(0);
  opacity: 1;
}
</style>
